import Vue from 'vue'
import { $_bridge, $_localhost, getURL } from '@/nms/plugins/bridge.js'
// import mqtt from 'mqtt'
import mqtt from 'mqtt/dist/mqtt.esm'

// Типы для мониторинга
interface MqttState {
  client: mqtt.MqttClient | null
  topic: string[]
  records: Record<string, any[]>
}

interface RecordsFilterFields {
  parentCls: any[]
  parentName: any[]
  'ss.state': any[]
  template: any[]
  'info.device_type': any[]
  'info.manufacturer': any[]
}

interface RecordsFilterFilter {
  parentCls: any | null
  parentName: any | null
  'ss.state': any | null
  template: any | null
  'info.device_type': any | null
  'info.manufacturer': any | null
}

interface RecordsFilter {
  fields: RecordsFilterFields
  filter: RecordsFilterFilter
  shouldBeUpdated: boolean
}

interface JournalState {
  counter: Record<string, any>
  recordsFilter: RecordsFilter
}

interface MonitoringState {
  dabLock: string[]
  mqtt: MqttState
  journal: JournalState
  serverConnect: boolean
  serverMeasures: Record<string, any>
  measure: Record<string, any>
  listener: Record<string, boolean>
  status: Record<string, any>
  statistics: Record<string, any>
}

// Начальное состояние
const state = (): MonitoringState => ({
  dabLock: [],
  mqtt: {
    client: null,
    topic: [],
    records: {}
  },
  journal: {
    counter: {},
    recordsFilter: {
      fields: {
        parentCls: [],
        parentName: [],
        'ss.state': [],
        template: [],
        'info.device_type': [],
        'info.manufacturer': [],
      },
      filter: {
        parentCls: null,
        parentName: null,
        'ss.state': null,
        template: null,
        'info.device_type': null,
        'info.manufacturer': null,
      },
      shouldBeUpdated: false,
    },
  },
  serverConnect: true,
  serverMeasures: {},
  measure: {},
  listener: {},
  status: {},
  statistics: {},
})

export default {
  state,

  mutations: {
    LOAD_COUNTERS(state: MonitoringState, data: { id: string; value: any }) {
      Vue.set(state.journal.counter, data.id, data.value)
    },

    LOAD_STATISTICS(state: MonitoringState, data: any) {
      Vue.set(state, 'statistics', data)
    },

    LOAD_MEASURES(state: MonitoringState, data: { $id: string; measures: any[] }) {
      const measure$: Record<string, any> = {}
      data.measures.forEach((measure, index) => {
        measure.index = index
        measure$[measure.$id] = measure
      })
      Vue.set(state.measure, data.$id, measure$)
    },

    ADD_STATUS_KEY(state: MonitoringState, data: { $id: string; state: string }) {
      if(!state.status[data.$id]) {
        Vue.set(state.status, data.$id, data.state)
      }
    },

    ADD_STATUS(state: MonitoringState, data: { $id: string; state: any }) {
      Vue.set(state.status, data.$id, data.state)
    },

    SERVER_PING(state: MonitoringState, data: boolean) {
      state.serverConnect = data
    },
    
    SET_SERVER_MEASURE(state: MonitoringState, data: { id: string; value: any }) {
      Vue.set(state.serverMeasures, data.id, data.value)
    },

    SET_FILTER_FIELDS(state: MonitoringState, data: Partial<RecordsFilterFields>) {
      Object.keys(data).forEach((field) => {
        Vue.set(state.journal.recordsFilter.fields, field, data[field as keyof RecordsFilterFields])
      })
    },

    SET_COUNTER_STATUS_FILTER(state: MonitoringState, data: Partial<RecordsFilterFilter>) {
      Object.keys(data).forEach((field) => {
        Vue.set(state.journal.recordsFilter.filter, field, data[field as keyof RecordsFilterFilter])
      })
    },

    SHOULD_BE_UPDATED(state: MonitoringState, data: boolean) {
      Vue.set(state.journal.recordsFilter, 'shouldBeUpdated', data)
    },

    SET_MQTT_CLIENT(state: MonitoringState, data: mqtt.MqttClient | null) {
      Vue.set(state.mqtt, 'client', data)
    },

    SET_TOPIC(state: MonitoringState, topic: string) {
      if(!state.mqtt.records[topic]) {
        Vue.set(state.mqtt.records, topic, [])
      }    
    },

    SET_TOPIC_RECORD(state: MonitoringState, data: { topic: string; task: any }) {
      if(Array.isArray(data.task)) {
        Vue.set(state.mqtt.records, data.topic, data.task)
      } else {
        const t = state.mqtt.records[data.topic]?.findIndex((item) => item.id === data.task.id)
        if(t !== undefined && t >= 0) {
          state.mqtt.records[data.topic].splice(t, 1, data.task)
        } else {
          if(!state.mqtt.records[data.topic]) {
            Vue.set(state.mqtt.records, data.topic, [])
          }
          state.mqtt.records[data.topic].push(data.task)
        }
      }
    },

    SET_DABLOCK_ID(state: MonitoringState, id: string) {
      state.dabLock.push(id)
    },

    REMOVE_DABLOCK_ID(state: MonitoringState, id: string) {
      const index = state.dabLock.indexOf(id)
      if(index !== -1) {
        state.dabLock.splice(index, 1)
      }
    },
  },

  actions: {
    async serverPingSubscribe({ commit }: any) {
      $_bridge.on('disconnect', () => {
        commit('SERVER_PING', false)
      })
      $_bridge.on('connect', () => {
        commit('SERVER_PING', true)
      })
    },
    
    async setServerMeasure({ commit }: any, data: { id: string; value: any }) {
      commit('SET_SERVER_MEASURE', data)
    },

    async stopAutoRefresh({ commit, state }: any) {
      const journal = $_bridge.journal()
      journal.stopAutoRefresh()
    },

    async getMirrorUniqValues({ commit }: any) {
      const journal = $_bridge.journal()
      const data$ = await journal.getMirrorUniqValues([
        'parentCls',
        'parentName',
        'ss.state',
        'template',
        'info.device_type',
        'info.manufacturer'
      ])
      if(data$) {
        commit('SET_FILTER_FIELDS', data$)
      }
    },

    async loadCounters({ commit, dispatch }: any, data?: any) {
      try {
        dispatch('setLoader', {
          key: 'loadCounters',
          value: true,
        }, { root: true })
        
        const journal = $_bridge.journal()
        const counters = await journal.getCounters('records')
        
        if(counters) {
          commit('LOAD_COUNTERS', counters)
        }
        
        dispatch('setLoader', {
          key: 'loadCounters',
          value: false,
        }, { root: true })
      } catch(error) {
        console.error('STORE', 'JOURNAL', 'loadCounters', 'smth wrong', error)
        dispatch('setLoader', {
          key: 'loadCounters',
          value: false,
        }, { root: true })
      }
    },

    async statusCounterStatusFilter({ commit }: any, data: Partial<RecordsFilterFilter>) {
      commit('SET_COUNTER_STATUS_FILTER', data)
    },

    async shouldBeUpdated({ commit }: any, data: boolean) {
      commit('SHOULD_BE_UPDATED', data)
    },

    async loadStatistics({ commit }: any) {
      try {
        const custom = await $_bridge.customQuery(
          'cmdb',
          'services/statistics',
          {},
          'GET',
        )
        if(custom) {
          commit('LOAD_STATISTICS', custom)
        }
      } catch(error) {
        console.error('STORE', 'loadStatistics', 'smth wrong', error)
      }
    },

    measuresSubscribeInit({ state, dispatch }: any, data: { name: string; listener: string; group: string; handler?: (e: any) => void }) {
      const handler$ = (e: any) => {       
        if(e.$id) {
          dispatch(data.name, e)
        }
      }
      const handler = data.handler || handler$
      
      if(state.listener[data.listener]) return
      
      $_bridge.offStream(data.group)
      state.listener[data.listener] = true
      $_bridge.onStream(handler, data.group, (d: any) => d, true)
    },

    measuresSubscribeClose({ state }: any, data: { handler: string; group: string; listener: string }) {
      $_bridge.removeListener('data', data.handler)
      $_bridge.offStream(data.group)
      state.listener[data.listener] = false
    },

    async unsubscribeAll() {
      await $_bridge.unsubscribeAll()
    },

    async loadMeasures({ commit }: any, $id: string) {
      const obj$ = await $_bridge.getObjectById($id)
      if(obj$?.$id) {
        const measures$ = await obj$.getMeasures(true, false)
        const data$ = {
          $id: $id,
          measures: measures$
        }
        commit('LOAD_MEASURES', data$)
      }
    },

    async addStatusKey({ commit }: any, item: { $id?: string }) {
      if(item.$id) {
        if(item.$id === 'cabinet$7') {
          console.log('STORE', 'addStatusKey', 'cabinet$7', item)
        }
        const value = 'unknown'
        const statusObject$ = {
          $id: item.$id,
          state: value,
        }
        commit('ADD_STATUS_KEY', statusObject$)
      }
    },

    addStatus({ commit }: any, e: { $id?: string; state?: { infoState?: any; value?: any } }) {
      const status$ = e?.state?.infoState || e?.state?.value || e?.state || 'unknown'
      const status = {
        $id: e.$id,
        state: status$
      }
      commit('ADD_STATUS', status)
    },

    async mqttConnect({ commit }: any) {
      console.log('STORE', 'mqttConnect')
      try {
        const host = $_localhost ? '192.168.2.69' : getURL('bridge')
        // const host = $_localhost ? 'http://192.168.2.69' : getURL('bridge')
        const port = 443
        const options = {
          protocol: 'wss' as const,
          username: 'e-nms-ui-jwt-client',
          clientId: `UI_client_${Math.random().toString(16).substring(2, 10)}`,
          clean: true,
          keepalive: 0,
          resubscribe: true,
          reconnectPeriod: 10000,
          connectTimeout: 5000,
        }
        // options.password = await $_bridge.getAccessToken()
        options.password = await $_bridge.getMqttToken()
        // console.log('$_bridge.getMqttToken options', options)
        const client = await mqtt.connectAsync(`wss://${host}:${port}/mqtt`, options)
        console.log('STORE', 'mqtt', 'client', client)
        
        if(client) {
          commit('SET_MQTT_CLIENT', client)

          client.on('connect', () => {
            console.log('STORE', 'mqtt', 'connected')
          })

          client.on('reconnect', () => {
            console.log('STORE', 'mqtt', '--->reconnect')
          })

          client.on('error', (err) => {
            console.log('STORE', 'mqtt', '--->error', err)
          })

          client.on('offline', () => {
            console.log('STORE', 'mqtt', '--->offline')
          })

          client.on('message', (topic, message) => { 
            console.log('STORE', 'mqtt', '--->message', message)
            if(topic?.startsWith('e-nms/tasks')) {
              const task = JSON.parse(message.toString())
              commit('SET_TOPIC_RECORD', { topic: 'e-nms/tasks', task: task })
            }
            if(topic?.startsWith('e-nms/test')) {
              const task = JSON.parse(message.toString())
              commit('SET_TOPIC_RECORD', { topic: 'e-nms/test', task: task })
            }
          })         
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'mqttConnect', err)
      }   
    },

    async mqttSubscribeTopic({ getters }: any, topic: string) {
      try {
        const client = getters.getMqttClient
        console.log('STORE', 'mqttSubscribeTopic', 'client', client)
        if(client) {
          client.subscribe(topic, (err) => {
            console.log('STORE', 'mqttSubscribeTopic topic', topic, err)
          })
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'mqttSubscribeTopic', err)
      }   
    },

    async createDabLock({ commit }: any, id: string) {
      try {
        const block = await $_bridge.createDabLock(id)
        commit('SET_DABLOCK_ID', id)
        return block           
      } catch(err) {
        console.error('CATCH', 'STORE', 'createDabLock', err)
      }   
    },
    
    async disposeDabLock({ commit }: any, id: string) {
      try {
        if(id) {
          const block = await $_bridge.disposeDabLock(id)
          commit('REMOVE_DABLOCK_ID', id)
          return block
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'disposeDabLock', err)
      }   
    },

    async clearDabLock({ state, commit }: any) {
      try {
        for(const id of state.dabLock) {
          await $_bridge.disposeDabLock(id)
          commit('REMOVE_DABLOCK_ID', id)
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'clearDabLock', err)
      }   
    },
  },

  getters: {
    getStatus(state: MonitoringState) {
      return state.status
    },

    getCounters: (state: MonitoringState) => {
      return state.journal.counter
    },

    getFilterFields: (state: MonitoringState) => {
      return state.journal.recordsFilter.fields
    },

    getMqttClient(state: MonitoringState) {
      return state.mqtt.client
    },

    getTopicRecords: (state: MonitoringState) => (topic: string) => {
      return state.mqtt.records[topic]
    }
  },
}